<template>
  <div class="audio-playground bg-gray-50 dark:bg-gray-900 p-6">
    <!-- No Models Warning -->
    <Alert
      v-if="!isLoadingModels && ttsModels.length === 0 && sttModels.length === 0"
      variant="warning"
      class="mb-6"
    >
      No audio models available. Please check your API configuration.
    </Alert>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- TTS Section -->
      <Card padding="lg">
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
          Text-to-Speech
        </h3>

        <!-- TTS Model Picker -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Model</label
          >
          <Select
            :model-value="ttsSelectedModel"
            :options="ttsModelOptions"
            :disabled="isLoadingModels || ttsModels.length === 0"
            placeholder="Select TTS model"
            @update:model-value="handleTtsModelChange"
          />
        </div>

        <!-- Voice Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Voice</label
          >
          <Select
            :model-value="ttsVoice"
            :options="voiceOptions"
            placeholder="Select voice"
            @update:model-value="handleVoiceChange"
          />
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Response Format
          </label>
          <Select
            :model-value="ttsResponseFormat"
            :options="ttsResponseFormatOptions"
            placeholder="Select response format"
            @update:model-value="handleTtsResponseFormatChange"
          />
        </div>

        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Speed</label>
            <span class="text-sm text-gray-500 dark:text-gray-400">{{ ttsSpeed.toFixed(2) }}</span>
          </div>
          <input
            type="range"
            :value="ttsSpeed"
            min="0.25"
            max="4"
            step="0.05"
            class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            @input="handleTtsSpeedInput"
          />
        </div>

        <!-- Instructions: gated on metadata.supports_instructions (gpt-4o-mini-tts, Gemini TTS) -->
        <div v-if="ttsSupportsInstructions" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Instructions
          </label>
          <textarea
            v-model="ttsInstructions"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Optional: tone, style, pacing..."
          />
        </div>

        <!-- Language code: gated on metadata.supports_language_code (Gemini TTS) -->
        <div v-if="ttsSupportsLanguageCode" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Language code
          </label>
          <input
            v-model="ttsLanguageCode"
            type="text"
            placeholder="e.g. en, es, fr (ISO 639-1)"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <!-- Text Input -->
        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Text</label>
            <span
              class="text-sm"
              :class="
                ttsCharCount > 4096
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ ttsCharCount }}/4096
            </span>
          </div>
          <textarea
            v-model="ttsText"
            rows="4"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter text to convert to speech..."
          />
        </div>

        <!-- Generate Button -->
        <Button
          variant="primary"
          :disabled="!canGenerateTts || isTtsLoading"
          :loading="isTtsLoading"
          @click="handleGenerateTts"
          class="w-full"
        >
          Generate Speech
        </Button>

        <!-- Audio Player -->
        <div v-if="ttsAudioUrl" class="mt-4">
          <audio :src="ttsAudioUrl" controls class="w-full" />
        </div>
      </Card>

      <!-- STT Section -->
      <Card padding="lg">
        <h3
          class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
          Speech-to-Text
        </h3>

        <!-- STT Model Picker -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Model</label
          >
          <Select
            :model-value="sttSelectedModel"
            :options="sttModelOptions"
            :disabled="isLoadingModels || sttModels.length === 0"
            placeholder="Select STT model"
            @update:model-value="handleSttModelChange"
          />
        </div>

        <!-- File Upload -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Audio File</label
          >
          <div
            class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
            @click="triggerFileInput"
            @dragover.prevent
            @drop.prevent="handleFileDrop"
          >
            <input
              ref="fileInputRef"
              type="file"
              accept="audio/*"
              class="hidden"
              @change="handleFileSelect"
            />
            <svg
              class="w-8 h-8 mx-auto text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ sttFile ? sttFile.name : 'Click or drag audio file here' }}
            </p>
          </div>
        </div>

        <div class="mb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Settings</h4>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language (ISO-639-1)
            </label>
            <input
              v-model="sttLanguage"
              type="text"
              placeholder="e.g. en, es, fr"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >Prompt</label
            >
            <textarea
              v-model="sttPrompt"
              rows="3"
              placeholder="Optional guidance for style or continuation"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Format
            </label>
            <Select
              :model-value="sttResponseFormat"
              :options="sttResponseFormatOptions"
              placeholder="Select response format"
              @update:model-value="handleSttResponseFormatChange"
            />
          </div>

          <div class="mb-3">
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
                >Temperature</label
              >
              <span class="text-sm text-gray-500 dark:text-gray-400">{{
                sttTemperature.toFixed(1)
              }}</span>
            </div>
            <input
              type="range"
              :value="sttTemperature"
              min="0"
              max="1"
              step="0.1"
              class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              @input="handleSttTemperatureInput"
            />
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Timestamp Granularities
            </label>
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  class="accent-blue-600"
                  :checked="sttTimestampGranularities.includes('word')"
                  :disabled="sttResponseFormat !== 'verbose_json'"
                  @change="handleTimestampGranularityChange('word', $event)"
                />
                Word
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  class="accent-blue-600"
                  :checked="sttTimestampGranularities.includes('segment')"
                  :disabled="sttResponseFormat !== 'verbose_json'"
                  @change="handleTimestampGranularityChange('segment', $event)"
                />
                Segment
              </label>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Requires verbose_json response format
            </p>
          </div>

          <div class="mb-3 flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Stream</label>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Stream transcription events
              </p>
            </div>
            <Switch v-model="sttStream" />
          </div>

          <div
            v-if="sttStream && isSttLoading"
            class="mb-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
          >
            <div class="text-sm text-blue-700 dark:text-blue-300">Streaming transcription...</div>
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Chunking Strategy
            </label>
            <Select
              :model-value="sttChunkingMode"
              :options="sttChunkingModeOptions"
              placeholder="Select chunking strategy"
              @update:model-value="handleSttChunkingModeChange"
            />
          </div>

          <div v-if="sttChunkingMode === 'server_vad'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Prefix Padding (ms)
              </label>
              <input
                type="number"
                min="0"
                :value="sttVadPrefixPaddingMs"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleVadPrefixPaddingInput"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Silence Duration (ms)
              </label>
              <input
                type="number"
                min="0"
                :value="sttVadSilenceDurationMs"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleVadSilenceDurationInput"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Threshold (0.0–1.0)
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                :value="sttVadThreshold"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleVadThresholdInput"
              />
            </div>
          </div>
        </div>

        <!-- Transcribe Button -->
        <Button
          variant="primary"
          :disabled="!canTranscribe || isSttLoading"
          :loading="isSttLoading"
          @click="handleTranscribe"
          class="w-full"
        >
          Transcribe
        </Button>

        <!-- Transcription Result -->
        <div v-if="sttTranscription" class="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >Transcription</label
          >
          <p class="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{{ sttTranscription }}</p>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { Alert, Button, Select, Switch, Card } from '@asabdev/asab-ui';
  import { useAudioPlayground } from '../../composables/useAudioPlayground';
  import type { Model } from '../../types/models';

  interface Props {
    ttsModels: Model[];
    sttModels: Model[];
    isLoadingModels: boolean;
  }

  const props = defineProps<Props>();

  const {
    ttsText,
    ttsSelectedModel,
    ttsVoice,
    ttsSpeed,
    ttsResponseFormat,
    ttsInstructions,
    ttsLanguageCode,
    ttsAudioUrl,
    isTtsLoading,
    sttSelectedModel,
    sttTranscription,
    isSttLoading,
    sttLanguage,
    sttPrompt,
    sttResponseFormat,
    sttTemperature,
    sttTimestampGranularities,
    sttStream,
    sttChunkingStrategy,
    generateSpeech,
    transcribeAudio,
    setTtsModel,
    setSttModel,
    setTtsVoice,
    setSttTimestampGranularity,
    setSttChunkingMode,
    updateSttServerVadConfig,
  } = useAudioPlayground();

  const sttFile = ref<File | null>(null);
  const fileInputRef = ref<HTMLInputElement | null>(null);

  const currentTtsModel = computed(() =>
    props.ttsModels.find(m => m.id === ttsSelectedModel.value)
  );

  const metaArr = (m: Model | undefined, key: string): string[] => {
    const v = m?.metadata?.[key];
    return Array.isArray(v) ? (v as string[]) : [];
  };

  // Voices + formats come straight from the model's seeded metadata. If a
  // dropdown is empty, the seed is missing the hint — fix in seed_models.py,
  // not here.
  const voiceOptions = computed(() =>
    metaArr(currentTtsModel.value, 'voices').map(name => ({ value: name, label: name }))
  );

  const ttsResponseFormatOptions = computed(() =>
    metaArr(currentTtsModel.value, 'response_formats').map(name => ({ value: name, label: name }))
  );

  const ttsSupportsInstructions = computed(
    () => currentTtsModel.value?.metadata?.supports_instructions === true
  );
  const ttsSupportsLanguageCode = computed(
    () => currentTtsModel.value?.metadata?.supports_language_code === true
  );

  // Snap voice + format to values the model accepts whenever the selected TTS
  // model changes, so a Gemini voice isn't left submitted to OpenAI (or v/v).
  watch(currentTtsModel, (m: Model | undefined) => {
    if (!m) return;
    const voices = metaArr(m, 'voices');
    const defaultVoice = (m.metadata?.voice_default as string | undefined) ?? voices[0];
    if (voices.length && !voices.includes(ttsVoice.value)) {
      setTtsVoice(defaultVoice);
    }
    const formats = metaArr(m, 'response_formats');
    if (formats.length && !formats.includes(ttsResponseFormat.value)) {
      // ttsResponseFormat is a writable `computed` in the composable.
      (ttsResponseFormat as unknown as { value: string }).value = formats[0];
    }
  }, { immediate: true });

  const ttsModelOptions = computed(() =>
    props.ttsModels.map(m => ({ value: m.id, label: m.name }))
  );

  const sttModelOptions = computed(() =>
    props.sttModels.map(m => ({ value: m.id, label: m.name }))
  );

  const sttResponseFormatOptions = [
    { value: 'json', label: 'json' },
    { value: 'text', label: 'text' },
    { value: 'srt', label: 'srt' },
    { value: 'verbose_json', label: 'verbose_json' },
    { value: 'vtt', label: 'vtt' },
  ];

  const sttChunkingMode = computed<'none' | 'auto' | 'server_vad'>(() => {
    if (sttChunkingStrategy.value === 'auto') return 'auto';
    if (sttChunkingStrategy.value && typeof sttChunkingStrategy.value === 'object')
      return 'server_vad';
    return 'none';
  });

  const sttChunkingModeOptions = [
    { value: 'none', label: 'None' },
    { value: 'auto', label: 'Auto' },
    { value: 'server_vad', label: 'Server VAD' },
  ];

  const sttVadPrefixPaddingMs = computed(() => {
    if (sttChunkingStrategy.value && typeof sttChunkingStrategy.value === 'object') {
      return sttChunkingStrategy.value.prefix_padding_ms ?? '';
    }
    return '';
  });

  const sttVadSilenceDurationMs = computed(() => {
    if (sttChunkingStrategy.value && typeof sttChunkingStrategy.value === 'object') {
      return sttChunkingStrategy.value.silence_duration_ms ?? '';
    }
    return '';
  });

  const sttVadThreshold = computed(() => {
    if (sttChunkingStrategy.value && typeof sttChunkingStrategy.value === 'object') {
      return sttChunkingStrategy.value.threshold ?? '';
    }
    return '';
  });

  const ttsCharCount = computed(() => ttsText.value.length);

  const canGenerateTts = computed(() => {
    return (
      ttsText.value.trim().length > 0 &&
      ttsCharCount.value <= 4096 &&
      ttsSelectedModel.value &&
      ttsVoice.value
    );
  });

  const canTranscribe = computed(() => sttFile.value && sttSelectedModel.value);

  onMounted(() => {
    if (props.ttsModels.length > 0 && !ttsSelectedModel.value) {
      setTtsModel(props.ttsModels[0].id);
    }
    if (props.sttModels.length > 0 && !sttSelectedModel.value) {
      setSttModel(props.sttModels[0].id);
    }
  });

  const handleTtsModelChange = (value: string) => setTtsModel(value);
  const handleSttModelChange = (value: string) => setSttModel(value);
  const handleVoiceChange = (value: string) => setTtsVoice(value);

  const handleTtsResponseFormatChange = (value: string) => {
    ttsResponseFormat.value = value as 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm';
  };

  const handleTtsSpeedInput = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    ttsSpeed.value = Number.isFinite(value) ? value : 1.0;
  };

  const handleSttResponseFormatChange = (value: string) => {
    sttResponseFormat.value = value as 'json' | 'text' | 'srt' | 'verbose_json' | 'vtt';
  };

  const handleSttTemperatureInput = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    sttTemperature.value = Number.isFinite(value) ? value : 0;
  };

  const handleTimestampGranularityChange = (granularity: 'word' | 'segment', event: Event) => {
    const checked = (event.target as HTMLInputElement).checked;
    setSttTimestampGranularity(granularity, checked);
  };

  const handleSttChunkingModeChange = (value: string) => {
    setSttChunkingMode(value as 'none' | 'auto' | 'server_vad');
  };

  const handleVadPrefixPaddingInput = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = raw === '' ? undefined : parseInt(raw, 10);
    updateSttServerVadConfig({ prefix_padding_ms: Number.isFinite(parsed) ? parsed : undefined });
  };

  const handleVadSilenceDurationInput = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = raw === '' ? undefined : parseInt(raw, 10);
    updateSttServerVadConfig({ silence_duration_ms: Number.isFinite(parsed) ? parsed : undefined });
  };

  const handleVadThresholdInput = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = raw === '' ? undefined : parseFloat(raw);
    updateSttServerVadConfig({ threshold: Number.isFinite(parsed) ? parsed : undefined });
  };

  const handleGenerateTts = async () => {
    await generateSpeech(ttsText.value.trim());
  };

  const triggerFileInput = () => {
    fileInputRef.value?.click();
  };

  const handleFileSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      sttFile.value = input.files[0];
    }
  };

  const handleFileDrop = (event: DragEvent) => {
    const file = event.dataTransfer?.files[0];
    if (file && file.type.startsWith('audio/')) {
      sttFile.value = file;
    }
  };

  const handleTranscribe = async () => {
    if (sttFile.value) {
      await transcribeAudio(sttFile.value);
    }
  };
</script>
